import { Builder } from '@builder.io/react'
import { HeadingClass, HeadingType } from 'enums'
import React, { useEffect, useState } from 'react'
import defaultBackgroundImage from '../../../images/Wavybackground.svg'
import LinkButton, { LinkButtonProp, LinkButtonRegisterProps } from '../common/button'
import CustomHeading, { headingTypePropObj, headingClassPropObj } from '../common/heading'

enum TextAlignment {
  Left = 'left',
  Right = 'right',
  Center = 'center'
}

interface BgImageType {
  imageSrc: any
  bgFill: 'cover' | 'contain'
  imgAltText?: string
}

interface Props {
  backgroundColor: string
  headingText: string
  headingTextColor: string
  subTextEnabled: boolean
  subText: string
  subTextColor: string
  buttonOne: LinkButtonProp
  textAlignment: TextAlignment
  headingType: HeadingType
  headingClass: HeadingClass
  button: boolean
  backgroundImage: BgImageType
  mobileBackgroundImage: BgImageType
  recipeHeading: string
  recipeHeadingColor: string
  recipeHeadingClass: HeadingClass
  recipeHeadingType: HeadingType
}

const RecipeSpotlight = ({
  backgroundColor,
  headingText,
  headingTextColor,
  subTextEnabled,
  subText,
  subTextColor,
  buttonOne,
  textAlignment,
  headingType,
  headingClass,
  button,
  backgroundImage,
  mobileBackgroundImage,
  recipeHeading,
  recipeHeadingColor,
  recipeHeadingClass,
  recipeHeadingType
}: Props) => {
  const [isMobile, setIsMobile] = useState(false)

  const handleWindowSizeChange = () => {
    if (window.innerWidth <= 768) {
      setIsMobile(true)
    } else {
      setIsMobile(false)
    }
  }

  useEffect(() => {
    handleWindowSizeChange()
    window.addEventListener('resize', handleWindowSizeChange)

    return () => {
      window.removeEventListener('resize', handleWindowSizeChange)
    }
  }, [])

  const getSubText = () => {
    if (!subTextEnabled) return null

    return (
      <p className="mt-4 text-lg leading-6 text-indigo-200" style={{ color: subTextColor }}>
        {subText}
      </p>
    )
  }

  return (
    <div
      className={`bg-indigo-700 bg-no-repeat bg-${backgroundImage?.bgFill || 'contain'}`}
      style={{
        backgroundColor: backgroundColor,
        backgroundImage: !isMobile ? `url(${backgroundImage?.imageSrc})` : '',
        backgroundPosition: 'right bottom'
      }}
    >
      {isMobile && (
        <div className="my-2 flex h-full max-h-60 w-full">
          <img
            src={mobileBackgroundImage?.imageSrc}
            className={`w-full object-right-top object-${mobileBackgroundImage?.bgFill}`}
            alt={mobileBackgroundImage?.imgAltText}
          />
        </div>
      )}
      <div className="py-4 px-4 sm:px-6 md:my-auto md:w-2/3 md:py-10 lg:px-16" style={{ textAlign: textAlignment }}>
        <CustomHeading
          classList="text-white tracking-tight my-2"
          headingClass={headingClass}
          headingType={headingType}
          color={headingTextColor}
        >
          {headingText}
        </CustomHeading>
        <CustomHeading
          classList="text-white tracking-tight"
          headingClass={recipeHeadingClass}
          headingType={recipeHeadingType}
          color={recipeHeadingColor}
        >
          {recipeHeading}
        </CustomHeading>
        {getSubText()}
        {button && buttonOne && (
          <LinkButton
            buttonColor={buttonOne.buttonColor}
            buttonText={`${buttonOne.buttonText} `}
            buttonTextColor={buttonOne.buttonTextColor}
            loggedInButtonText={buttonOne?.loggedInButtonText}
            buttonUrl={buttonOne.buttonUrl}
            isLink={buttonOne.isLink}
            classList={
              'mt-8 w-full inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-full text-indigo-600 bg-white hover:bg-indigo-50 md:w-auto'
            }
          />
        )}
      </div>
    </div>
  )
}

export default RecipeSpotlight

Builder.registerComponent(RecipeSpotlight, {
  name: 'RecipeSpotlight',
  inputs: [
    {
      name: 'textAlignment',
      type: 'string',
      enum: ['left', 'right', 'center'],
      defaultValue: 'center'
    },
    {
      name: 'backgroundColor',
      type: 'color',
      defaultValue: 'lightblue'
    },
    {
      ...headingTypePropObj
    },
    {
      ...headingClassPropObj,
      defaultValue: 'H3'
    },
    {
      name: 'headingText',
      type: 'text',
      defaultValue: 'Boost your productivity. Start using workflow today.'
    },
    {
      name: 'headingTextColor',
      type: 'color',
      defaultValue: 'white'
    },
    {
      name: 'recipeHeading',
      type: 'text',
      defaultValue: "Shepherd's pir"
    },
    {
      name: 'recipeHeadingColor',
      type: 'color',
      defaultValue: 'white'
    },
    {
      ...headingTypePropObj,
      name: 'recipeHeadingType'
    },
    {
      ...headingClassPropObj,
      name: 'recipeHeadingClass'
    },
    {
      name: 'subTextEnabled',
      type: 'boolean',
      defaultValue: true
    },
    {
      name: 'subText',
      type: 'text',
      defaultValue:
        'Varius facilisi mauris sed sit. Non sed et duis dui leo, vulputate id malesuada non. Cras aliquet purus dui laoreet diam sed lacus, fames.'
    },
    {
      name: 'subTextColor',
      type: 'color',
      defaultValue: 'white'
    },
    {
      ...LinkButtonRegisterProps,
      name: 'buttonOne'
    },
    {
      name: 'button',
      type: 'boolean',
      defaultValue: true
    },
    {
      name: 'backgroundImage',
      type: 'object',
      defaultValue: { imageSrc: defaultBackgroundImage, bgFill: 'contain' },
      subFields: [
        {
          name: 'imageSrc',
          type: 'file',
          defaultValue: defaultBackgroundImage
        },
        {
          name: 'bgFill',
          type: 'text',
          enum: ['cover', 'contain'],
          defaultValue: 'contain'
        }
      ]
    },
    {
      name: 'mobileBackgroundImage',
      type: 'object',
      defaultValue: { imageSrc: null, bgFill: 'contain', imgAltText: '' },
      subFields: [
        {
          name: 'imageSrc',
          type: 'file',
          defaultValue: null
        },
        {
          name: 'bgFill',
          type: 'text',
          enum: ['cover', 'contain'],
          defaultValue: 'contain'
        },
        {
          name: 'imgAltText',
          type: 'text',
          defaultValue: ''
        }
      ]
    }
  ]
})
